/**
 * ImageLightboxTW
 *
 * Zentrale Vollbild-Bildanzeige mit Double-Tap-Zoom, Pinch-Zoom
 * und optionaler Galerie-Navigation. Kein externes Zoom-Library --
 * eigene Touch-Erkennung mit CSS-Transforms.
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { XCircle, ChevronLeft, ChevronRight } from 'lucide-react';

const DOUBLE_TAP_SCALE = 3.5;
const MAX_SCALE = 10;

interface ImageLightboxProps {
  isOpen: boolean;
  imageUrl: string;
  caption?: string;
  alt?: string;
  allImages?: Array<{ src: string; caption?: string; alt?: string }>;
  currentIndex?: number;
  onClose: () => void;
}

export const ImageLightboxTW: React.FC<ImageLightboxProps> = ({
  isOpen,
  imageUrl,
  caption,
  alt,
  allImages,
  currentIndex: initialIndex = 0,
  onClose
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isClosing, setIsClosing] = useState(false);
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [dragOffsetPct, setDragOffsetPct] = useState(0); // Track-Verschiebung in % während Swipe
  const [isDragging, setIsDragging] = useState(false);
  const [imgAspect, setImgAspect] = useState<number | null>(null);
  const [viewport, setViewport] = useState(() => ({
    w: typeof window !== 'undefined' ? window.innerWidth : 1024,
    h: typeof window !== 'undefined' ? window.innerHeight : 768,
  }));
  const [isMousePanning, setIsMousePanning] = useState(false);
  const lastTapRef = useRef(0);
  const pinchStartDistRef = useRef<number | null>(null);
  const pinchStartScaleRef = useRef(1);
  const panStartRef = useRef<{ x: number; y: number; tx: number; ty: number } | null>(null);
  const swipeStartRef = useRef<{ x: number; y: number; time: number; axis: 'x' | 'y' | null } | null>(null);
  const isAnimatingRef = useRef(false);
  const scaleRef = useRef(1);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const trackContainerRef = useRef<HTMLDivElement | null>(null);

  // Reset beim Öffnen
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
      setIsClosing(false);
      setScale(1);
      setTranslate({ x: 0, y: 0 });
      setDragOffsetPct(0);
      setIsDragging(false);
      scaleRef.current = 1;
    }
  }, [isOpen, initialIndex]);

  // Bild-Daten
  const currentImage = allImages && allImages[currentIndex]
    ? allImages[currentIndex]!
    : { src: imageUrl, caption, alt };
  const hasMultipleImages = allImages && allImages.length > 1;

  // Navigation
  const goToPrevious = useCallback(() => {
    if (allImages && currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setScale(1);
      setTranslate({ x: 0, y: 0 });
      scaleRef.current = 1;
    }
  }, [currentIndex, allImages]);

  const goToNext = useCallback(() => {
    if (allImages && currentIndex < allImages.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setScale(1);
      setTranslate({ x: 0, y: 0 });
      scaleRef.current = 1;
    }
  }, [currentIndex, allImages]);

  // Aspect-Ratio des aktiven Bildes erfassen, um die Spalte präzise auf die
  // tatsächliche Bildbreite zu sizen. Damit liegen Pfeile und X exakt an
  // den Bildkanten.
  useEffect(() => {
    if (!isOpen) return;
    const img = imgRef.current;
    if (!img) return;
    const update = () => {
      if (img.naturalWidth && img.naturalHeight) {
        setImgAspect(img.naturalWidth / img.naturalHeight);
      }
    };
    if (img.complete) update();
    img.addEventListener('load', update);
    return () => {
      img.removeEventListener('load', update);
    };
  }, [isOpen, currentIndex, currentImage.src]);

  // Viewport-Tracking, damit sich die Bild-Spalte bei Resize anpasst.
  useEffect(() => {
    if (!isOpen) return;
    const onResize = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [isOpen]);

  // Keyboard
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') goToPrevious();
      else if (e.key === 'ArrowRight') goToNext();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, goToPrevious, goToNext]);

  // Scroll-Sperre OHNE jegliche Body- oder HTML-Style-Änderung.
  //
  // Frühere Ansätze (position:fixed body, overflow:hidden auf body/html)
  // verursachten einen sichtbaren Layout-Shift beim Schließen: Body wechselt
  // von fixed zurück zu static, dabei paint​et der Browser kurz mit
  // scrollTop=0 -- das wirkt wie "Beitrag/Newsfeed laden gerade nach". Mit
  // Lenis (Smooth-Scroll) wird das Problem zusätzlich verstärkt, weil Lenis
  // nach dem Restore intern resyncen muss.
  //
  // Stattdessen: Lenis pausieren + Wheel/Touchmove preventDefault. Damit ist
  // kein einziger Style am Body/HTML modifiziert -- es gibt schlicht keinen
  // anderen Frame, der gepaintet werden könnte.
  useEffect(() => {
    if (!isOpen) return;
    type LenisLike = { stop: () => void; start: () => void };
    const lenis = (window as { __lenis?: LenisLike }).__lenis ?? null;

    if (lenis) lenis.stop();

    // Wheel/Touchmove auf Document-Ebene blocken -- die Lightbox liegt im
    // Portal auf body-Ebene und hat ihre eigenen Touch-Handler, die per
    // stopPropagation/preventDefault selbst entscheiden.
    const preventScroll = (e: Event) => {
      // Im Lightbox-Bereich (z.B. Thumbnail-Strip mit overflow-x-auto)
      // soll Scrolling weiterhin möglich sein. Die Lightbox liegt unter
      // dem Modal-Container mit z-[10002].
      const target = e.target as Element | null;
      if (target?.closest?.('[data-image-lightbox]')) return;
      e.preventDefault();
    };
    const preventScrollKeys = (e: KeyboardEvent) => {
      const scrollKeys = ['PageUp', 'PageDown', 'Home', 'End', 'ArrowUp', 'ArrowDown', ' ', 'Spacebar'];
      if (scrollKeys.includes(e.key)) {
        e.preventDefault();
      }
    };

    document.addEventListener('wheel', preventScroll, { passive: false });
    document.addEventListener('touchmove', preventScroll, { passive: false });
    document.addEventListener('keydown', preventScrollKeys);

    return () => {
      if (lenis) lenis.start();
      document.removeEventListener('wheel', preventScroll);
      document.removeEventListener('touchmove', preventScroll);
      document.removeEventListener('keydown', preventScrollKeys);
    };
  }, [isOpen]);

  // Double-Tap/Double-Click: Wenn gezoomt -> zurück, wenn nicht -> reinzoomen
  const handleImgClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    const now = Date.now();
    if (now - lastTapRef.current < 400) {
      isAnimatingRef.current = true;
      const resetToOriginal = scaleRef.current > 1.05;
      const newScale = resetToOriginal ? 1 : DOUBLE_TAP_SCALE;
      setScale(newScale);
      setTranslate({ x: 0, y: 0 });
      scaleRef.current = newScale;
      lastTapRef.current = 0;
      setTimeout(() => { isAnimatingRef.current = false; }, 300);
    } else {
      lastTapRef.current = now;
    }
  }, []);

  // Pinch-Zoom: Abstand zwischen zwei Fingern tracken
  const getTouchDistance = (touches: React.TouchList) => {
    const dx = touches[0]!.clientX - touches[1]!.clientX;
    const dy = touches[0]!.clientY - touches[1]!.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Berechnet wie weit das Bild maximal verschoben werden darf
  const getPanBounds = useCallback(() => {
    if (!imgRef.current) return { x: 0, y: 0 };
    const rect = imgRef.current.getBoundingClientRect();
    const imgW = rect.width / scaleRef.current;
    const imgH = rect.height / scaleRef.current;
    const maxX = Math.max(0, (imgW * scaleRef.current - window.innerWidth) / 2);
    const maxY = Math.max(0, (imgH * scaleRef.current - window.innerHeight) / 2);
    return { x: maxX, y: maxY };
  }, []);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      // Pinch-Start
      pinchStartDistRef.current = getTouchDistance(e.touches);
      pinchStartScaleRef.current = scaleRef.current;
      panStartRef.current = null;
      swipeStartRef.current = null;
    } else if (e.touches.length === 1) {
      if (scaleRef.current > 1.05) {
        // Pan-Start (nur wenn gezoomt)
        panStartRef.current = {
          x: e.touches[0]!.clientX,
          y: e.touches[0]!.clientY,
          tx: translate.x,
          ty: translate.y,
        };
      } else {
        // Swipe-Start (nur im unzoomed State -> Navigation durch die Galerie)
        swipeStartRef.current = {
          x: e.touches[0]!.clientX,
          y: e.touches[0]!.clientY,
          time: Date.now(),
          axis: null,
        };
        setIsDragging(true);
      }
    }
  }, [translate]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2 && pinchStartDistRef.current !== null) {
      // Pinch-Zoom
      e.preventDefault();
      const currentDist = getTouchDistance(e.touches);
      const ratio = currentDist / pinchStartDistRef.current;
      const newScale = Math.min(MAX_SCALE, Math.max(1, pinchStartScaleRef.current * ratio));
      setScale(newScale);
      scaleRef.current = newScale;
    } else if (e.touches.length === 1 && panStartRef.current && scaleRef.current > 1.05) {
      // Pan (Ein-Finger-Verschiebung) mit Begrenzung
      e.preventDefault();
      const dx = e.touches[0]!.clientX - panStartRef.current.x;
      const dy = e.touches[0]!.clientY - panStartRef.current.y;
      const newX = panStartRef.current.tx + dx;
      const newY = panStartRef.current.ty + dy;
      const bounds = getPanBounds();
      setTranslate({
        x: Math.max(-bounds.x, Math.min(bounds.x, newX)),
        y: Math.max(-bounds.y, Math.min(bounds.y, newY)),
      });
    } else if (
      e.touches.length === 1 &&
      swipeStartRef.current &&
      scaleRef.current <= 1.05 &&
      allImages &&
      allImages.length > 1
    ) {
      // Swipe-Drag im unzoomed State -> Track live verschieben.
      const dx = e.touches[0]!.clientX - swipeStartRef.current.x;
      const dy = e.touches[0]!.clientY - swipeStartRef.current.y;

      // Achse beim ersten signifikanten Move festlegen.
      if (!swipeStartRef.current.axis && Math.abs(dx) + Math.abs(dy) > 10) {
        swipeStartRef.current.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      }
      if (swipeStartRef.current.axis !== 'x') return;

      if (e.cancelable) e.preventDefault();
      const containerWidth = trackContainerRef.current?.clientWidth ?? window.innerWidth;
      if (containerWidth === 0) return;

      // Gummiband am Rand (iOS-Style): Widerstand an Anfang/Ende.
      let effectiveDx = dx;
      if (
        (currentIndex === 0 && dx > 0) ||
        (currentIndex === allImages.length - 1 && dx < 0)
      ) {
        effectiveDx = dx * 0.3;
      }
      setDragOffsetPct((effectiveDx / containerWidth) * 100);
    }
  }, [allImages, currentIndex]);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    pinchStartDistRef.current = null;
    panStartRef.current = null;

    // Swipe-Auswertung im unzoomed State: Snap je nach Distanz/Geschwindigkeit.
    if (swipeStartRef.current && scaleRef.current <= 1.05 && allImages && allImages.length > 1) {
      const touch = e.changedTouches[0];
      if (touch && swipeStartRef.current.axis === 'x') {
        const dx = touch.clientX - swipeStartRef.current.x;
        const dt = Date.now() - swipeStartRef.current.time;
        const containerWidth = trackContainerRef.current?.clientWidth ?? window.innerWidth;
        const thresholdDistance = containerWidth * 0.2;
        const isQuickSwipe = Math.abs(dx) > 40 && dt < 300;

        if (Math.abs(dx) > thresholdDistance || isQuickSwipe) {
          if (dx < 0 && currentIndex < allImages.length - 1) {
            setCurrentIndex(currentIndex + 1);
            setScale(1);
            setTranslate({ x: 0, y: 0 });
            scaleRef.current = 1;
          } else if (dx > 0 && currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
            setScale(1);
            setTranslate({ x: 0, y: 0 });
            scaleRef.current = 1;
          }
        }
      }
      swipeStartRef.current = null;
    }

    // Drag-Offset zurueck auf 0 (Transition sorgt fuer Snap-Animation).
    setIsDragging(false);
    setDragOffsetPct(0);

    // Wenn zurück auf 1x gezoomt, Position zurücksetzen
    if (scaleRef.current <= 1.05) {
      setScale(1);
      setTranslate({ x: 0, y: 0 });
      scaleRef.current = 1;
    }
  }, [allImages, currentIndex]);

  // Maus-Pan im Zoom-Modus: Klick + Halten + Bewegen verschiebt das Bild.
  // Mousedown startet das Pan, globale Listener (siehe useEffect) tracken
  // Move/Up auch wenn der Cursor das Bild verlässt.
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (scaleRef.current <= 1.05) return;
    if (e.button !== 0) return; // nur linke Maustaste
    e.preventDefault();
    panStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      tx: translate.x,
      ty: translate.y,
    };
    setIsMousePanning(true);
  }, [translate]);

  useEffect(() => {
    if (!isOpen) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!panStartRef.current || scaleRef.current <= 1.05) return;
      e.preventDefault();
      const dx = e.clientX - panStartRef.current.x;
      const dy = e.clientY - panStartRef.current.y;
      const newX = panStartRef.current.tx + dx;
      const newY = panStartRef.current.ty + dy;
      const bounds = getPanBounds();
      setTranslate({
        x: Math.max(-bounds.x, Math.min(bounds.x, newX)),
        y: Math.max(-bounds.y, Math.min(bounds.y, newY)),
      });
    };

    const handleMouseUp = () => {
      if (panStartRef.current) {
        panStartRef.current = null;
        setIsMousePanning(false);
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isOpen, getPanBounds]);

  // Schließen-Animation
  const animateClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(onClose, 200);
  }, [onClose]);

  if (!isOpen) return null;

  const isZoomed = scale > 1.05;

  // Spalten-Breite exakt auf das Bild dimensionieren, damit Pfeile direkt
  // an den Bildkanten und das X über der oberen rechten Ecke sitzen.
  const arrowReserve = hasMultipleImages && !isZoomed ? 140 : 16;
  const availableWidth = Math.max(120, viewport.w - arrowReserve);
  const availableHeight = Math.max(120, viewport.h - 130);
  const targetWidth = imgAspect
    ? Math.min(availableWidth, availableHeight * imgAspect)
    : availableWidth;
  const slideHeight = Math.min(availableHeight, imgAspect ? targetWidth / imgAspect : availableHeight);

  return ReactDOM.createPortal(
    <div
      data-image-lightbox=""
      className="fixed inset-0 z-[10002] flex items-center justify-center"
      style={{ touchAction: 'none' }}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black"
        onClick={animateClose}
        style={{
          opacity: isClosing ? 0 : 0.92,
          transition: 'opacity 0.2s ease-out',
        }}
      />

      {/* Close-Button -- wird im Bild-Container relativ positioniert */}

      {/* Bild-Counter */}
      {hasMultipleImages && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 px-3 py-1 rounded-full bg-black/50 text-white text-sm font-medium pointer-events-none">
          {currentIndex + 1} / {allImages!.length}
        </div>
      )}

      {/* Wrapper: inline-flex damit nur die tatsächliche Bild- und Pfeilbreite
          klickblockierend wird. Klicks neben dem Bild treffen den Backdrop und
          schließen die Lightbox. */}
      <div
        className="relative z-[1] inline-flex items-center gap-2 sm:gap-3 max-w-full"
        style={{
          opacity: isClosing ? 0 : 1,
          transition: 'opacity 0.2s ease-out',
        }}
        onClick={(e) => {
          // Klicks in den schmalen Lücken zwischen Pfeilen und Bild-Spalte
          // schließen ebenfalls (wie der Backdrop).
          if (e.target === e.currentTarget) animateClose();
        }}
      >
        {/* Linker Pfeil mit großer Trefferfläche */}
        {hasMultipleImages && !isZoomed && (
          <button
            onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
            disabled={currentIndex === 0}
            className="flex-shrink-0 p-3 sm:p-4 rounded-full
              bg-black/60 hover:bg-black/80 backdrop-blur-sm
              border border-white/30 hover:border-white/50
              shadow-lg shadow-black/40
              disabled:opacity-20 disabled:cursor-not-allowed
              transition-all"
            style={{ minWidth: 56, minHeight: 56 }}
            aria-label="Vorheriges Bild"
          >
            <ChevronLeft className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
          </button>
        )}

        {/* Bild-Spalte: Breite auf Bild abgestimmt */}
        <div
          className="flex flex-col items-stretch flex-shrink-0"
          style={{ width: `${targetWidth}px`, maxWidth: '100%' }}
        >
          {/* X über der oberen rechten Ecke des Bildes (separate Zeile) */}
          {!isZoomed && (
            <div className="flex justify-end mb-2">
              <button
                onClick={(e) => { e.stopPropagation(); animateClose(); }}
                className="transition-opacity hover:opacity-80 active:opacity-60"
                style={{ minWidth: 44, minHeight: 44 }}
                aria-label="Schließen"
              >
                <XCircle className="w-9 h-9 text-white/80" />
              </button>
            </div>
          )}

          <div
            ref={trackContainerRef}
            className="relative overflow-hidden w-full"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {/* Track: alle Bilder nebeneinander, Transform per currentIndex +
                Live-Drag-Offset. Transition nur wenn NICHT aktiv gedragged wird. */}
            {hasMultipleImages ? (
              <div
                className="flex"
                style={{
                  transform: `translateX(calc(-${currentIndex * 100}% + ${dragOffsetPct}%))`,
                  transition: isDragging
                    ? 'none'
                    : 'transform 380ms cubic-bezier(0.32, 0.72, 0, 1)',
                  willChange: 'transform',
                }}
              >
                {allImages!.map((img, i) => {
                  const isActive = i === currentIndex;
                  return (
                    <div
                      key={i}
                      className="flex-shrink-0 w-full flex items-center justify-center"
                      style={{ height: `${slideHeight}px` }}
                    >
                      <img
                        ref={isActive ? imgRef : undefined}
                        src={img.src}
                        alt={img.alt || `Bild ${i + 1}`}
                        className="max-w-full max-h-full object-contain select-none"
                        draggable={false}
                        onClick={isActive ? handleImgClick : undefined}
                        onMouseDown={isActive ? handleMouseDown : undefined}
                        style={
                          isActive
                            ? {
                                transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
                                transformOrigin: 'center center',
                                transition: isAnimatingRef.current
                                  ? 'transform 0.25s ease-out'
                                  : 'none',
                                cursor: isZoomed
                                  ? (isMousePanning ? 'grabbing' : 'grab')
                                  : 'zoom-in',
                              }
                            : undefined
                        }
                      />
                    </div>
                  );
                })}
              </div>
            ) : (
              <img
                ref={imgRef}
                src={currentImage.src}
                alt={currentImage.alt || 'Vergrößertes Bild'}
                className="max-w-full object-contain select-none mx-auto block"
                draggable={false}
                onClick={handleImgClick}
                onMouseDown={handleMouseDown}
                style={{
                  maxHeight: `${slideHeight}px`,
                  transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
                  transformOrigin: 'center center',
                  transition: isAnimatingRef.current ? 'transform 0.25s ease-out' : 'none',
                  cursor: isZoomed
                    ? (isMousePanning ? 'grabbing' : 'grab')
                    : 'zoom-in',
                }}
              />
            )}
          </div>

          {/* Caption direkt unter dem Bild */}
          {currentImage.caption && !isZoomed && (
            <div className="mt-2 mx-auto px-4 py-2 rounded-lg bg-black/60 text-white text-sm text-center max-w-full pointer-events-none">
              {currentImage.caption}
            </div>
          )}
        </div>

        {/* Rechter Pfeil mit großer Trefferfläche */}
        {hasMultipleImages && !isZoomed && (
          <button
            onClick={(e) => { e.stopPropagation(); goToNext(); }}
            disabled={currentIndex === allImages!.length - 1}
            className="flex-shrink-0 p-3 sm:p-4 rounded-full
              bg-black/60 hover:bg-black/80 backdrop-blur-sm
              border border-white/30 hover:border-white/50
              shadow-lg shadow-black/40
              disabled:opacity-20 disabled:cursor-not-allowed
              transition-all"
            style={{ minWidth: 56, minHeight: 56 }}
            aria-label="Nächstes Bild"
          >
            <ChevronRight className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
          </button>
        )}
      </div>

      {/* Thumbnail-Strip (Galerie) */}
      {hasMultipleImages && !isZoomed && (
        <div
          className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10
            flex gap-2 p-2 rounded-lg bg-black/40 max-w-[90vw] overflow-x-auto"
          onClick={(e) => e.stopPropagation()}
          style={{ opacity: isClosing ? 0 : 1, transition: 'opacity 0.2s' }}
        >
          {allImages!.map((img, index) => (
            <button
              key={index}
              onClick={() => {
                setCurrentIndex(index);
                setScale(1);
                setTranslate({ x: 0, y: 0 });
                scaleRef.current = 1;
              }}
              className={`
                relative w-14 h-10 rounded overflow-hidden shrink-0 transition-all
                ${index === currentIndex
                  ? 'ring-2 ring-brand ring-offset-1 ring-offset-black/50'
                  : 'opacity-50 hover:opacity-90'
                }
              `}
            >
              <img
                src={img.src}
                alt={img.alt || `Bild ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>,
    document.body
  );
};

export default ImageLightboxTW;
